import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import { Tile } from '@rossum/components/tile';
import { IconTrash } from '@rossum/ui/icons/tabler';
import { Stack, Typography } from '@rossum/ui/material';

type ConnectedQueueTileProps = {
  queue: Queue;
  workspace?: Workspace;
  connectedFieldsCount?: number;
  totalFieldsCount?: number;
  onRemove?: (url: Url) => void;

  dataCy: string;
};

export const ConnectedQueueTile = ({
  queue,
  workspace,
  connectedFieldsCount,
  totalFieldsCount,
  onRemove,
  dataCy,
}: ConnectedQueueTileProps) => {
  const intl = useIntl();

  return (
    <Tile
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          {workspace && (
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              {workspace.name}
            </Typography>
          )}
          <Typography variant="h6">{queue.name}</Typography>
        </Stack>
      }
      description={`${intl.formatMessage({
        id: 'features.engines.engineDetail.basicInformation.field.id',
      })}: ${queue.id}`}
      inlineBadges={
        totalFieldsCount !== undefined &&
        connectedFieldsCount !== totalFieldsCount
          ? [
              {
                key: 'connected-fields',
                label: intl.formatMessage(
                  {
                    id: 'features.engines.engineDetail.connectedQueues.connectedFields',
                  },
                  {
                    connectedFieldsCount,
                    totalFieldsCount,
                  }
                ),
                color: 'warning' as const,
              },
            ]
          : undefined
      }
      quickActions={
        onRemove
          ? [
              {
                key: 'remove',
                iconProps: { Icon: IconTrash, state: false, disabled: false },
                onClick: () => onRemove(queue.url),
              },
            ]
          : undefined
      }
      to={`/queues/${queue.id}/settings/basic`}
      data-cy={dataCy}
      data-id={String(queue.id)}
    />
  );
};
